import type { Data } from "@eai-toolkit/client";
import { Add } from "@mui/icons-material";
import { Stack } from "@mui/material";
import React, { useCallback, useMemo, useState } from "react";

import { LOCAL_STORAGE_KEY_RECENTLY_CHOSEN_DATA_OBJECTS } from "@helpers/constants";

import { useLegacyRecentsMigration } from "../../../hooks/useLegacyRecentsMigration";
import { useGetDataObjectByIdQuery } from "../../../store/api/dataApi";
import {
  ACCESS_MODE_READ_ONLY,
  ACCESS_MODE_READ_WRITE,
  type AccessMode,
  isAccessMode,
} from "../constants";
import DataMountsFieldRow, { getOptionLabel } from "./DataMountsFieldRow";
import { AddRowButton } from "./NewJobFormLayout";

type Props = {
  values: string[];
  onUpdate: (values: string[]) => void;
};

type MountObject = {
  dataId: string;
  sourcePath: string;
  mountPath: string;
  accessMode: AccessMode;
};

function parseMount(m: string): MountObject {
  const parts = m.split(":");
  let data = "";
  let mountPath = "";
  let rawAccessMode = "";
  if (parts.length === 4 && isAccessMode(parts[1])) {
    // 4-part: data:<srcMode>:<mountPath>:<dstMode> — discard parts[1].
    [data, , mountPath, rawAccessMode] = parts;
  } else {
    // 2- or 3-part: data:<mountPath>[:<dstMode>]
    [data = "", mountPath = "", rawAccessMode = ""] = parts;
  }
  const slashIndex = data.indexOf("/");
  const dataId = slashIndex === -1 ? data : data.slice(0, slashIndex);
  const sourcePath = slashIndex === -1 ? "" : data.slice(slashIndex);
  // Default unsuffixed legacy mounts to RW
  const accessMode: AccessMode = isAccessMode(rawAccessMode)
    ? rawAccessMode
    : ACCESS_MODE_READ_WRITE;
  return { dataId, sourcePath, mountPath, accessMode };
}

function stringifyMounts(mounts: MountObject[]) {
  return mounts.map((mount) => {
    const data = mount.sourcePath
      ? `${mount.dataId}${mount.sourcePath}`
      : mount.dataId;
    return [data, mount.mountPath, mount.accessMode].join(":");
  });
}

let nextRowKey = 0;
function getUniqueRowKey() {
  return nextRowKey++;
}

const DataMountsField = React.memo(function DataMountsField({
  values,
  onUpdate,
}: Props) {
  const mounts: MountObject[] = useMemo(() => values.map(parseMount), [values]);

  // Resolved Data object per row, reported up by each row from its own by-id
  // fetch (used only to hydrate mount-path auto-naming; see handleDataResolved).
  const [resolvedByIndex, setResolvedByIndex] = useState<
    Record<number, Data | null>
  >({});

  const handleDataResolved = useCallback((index: number, data: Data | null) => {
    setResolvedByIndex((prev) =>
      prev[index] === data ? prev : { ...prev, [index]: data },
    );
  }, []);

  // One-time migration from the old comma-joined-IDs recents format: resolve
  // up to the legacy cap of 3 ids into full Data objects so ResourceSelector
  // can seed them into its shared bookmarks cache.
  const { legacyIds, clearLegacyRecents } = useLegacyRecentsMigration(
    LOCAL_STORAGE_KEY_RECENTLY_CHOSEN_DATA_OBJECTS,
  );
  const [legacyId0, legacyId1, legacyId2] = legacyIds;
  const { data: legacyData0 } = useGetDataObjectByIdQuery(
    { id: legacyId0 || "" },
    { skip: !legacyId0 },
  );
  const { data: legacyData1 } = useGetDataObjectByIdQuery(
    { id: legacyId1 || "" },
    { skip: !legacyId1 },
  );
  const { data: legacyData2 } = useGetDataObjectByIdQuery(
    { id: legacyId2 || "" },
    { skip: !legacyId2 },
  );
  const initialRecents = useMemo(
    () =>
      [legacyData0, legacyData1, legacyData2].filter((d): d is Data =>
        Boolean(d),
      ),
    [legacyData0, legacyData1, legacyData2],
  );

  const updateMount = useCallback(
    (index: number, patch: Partial<MountObject>) => {
      onUpdate(
        stringifyMounts(
          mounts.map((m, i) => (i === index ? { ...m, ...patch } : m)),
        ),
      );
    },
    [mounts, onUpdate],
  );

  const handleDataObjectSelected = useCallback(
    (selection: Data | string | null, index: number) => {
      // Only a resolved Data object (picked from search results) drives the
      // mount-path auto-fill, matching the old map-resolution gate — an
      // unresolved freeSolo string leaves the mount path untouched.
      const resolvedSelection =
        typeof selection === "string" ? null : selection;
      const dataId =
        resolvedSelection?.id ??
        (typeof selection === "string" ? selection : "");
      const oldResolved = resolvedByIndex[index];
      const oldLabel =
        oldResolved && mounts[index]?.dataId === oldResolved.id
          ? getOptionLabel(oldResolved)
          : null;

      const updatedMounts = mounts.map((mount, i) =>
        i === index
          ? {
              dataId,
              sourcePath: mount.sourcePath,
              mountPath:
                // if the user customized the old pathname, leave it alone
                ((!mount.dataId ||
                  !oldLabel ||
                  mount.mountPath !== `/${oldLabel}`) &&
                  mount.mountPath) ||
                (resolvedSelection
                  ? `/${getOptionLabel(resolvedSelection)}`
                  : ""),
              accessMode: mount.accessMode,
            }
          : mount,
      );
      onUpdate(stringifyMounts(updatedMounts));
    },
    [mounts, resolvedByIndex, onUpdate],
  );

  const [rowKeys, setRowKeys] = useState(() => mounts.map(getUniqueRowKey));

  const handleAdd = useCallback(() => {
    setRowKeys((keys) => keys.concat(getUniqueRowKey()));
    // New mounts default to RO (least-privilege)
    onUpdate(
      stringifyMounts(
        mounts.concat({
          dataId: "",
          sourcePath: "",
          mountPath: "",
          accessMode: ACCESS_MODE_READ_ONLY,
        }),
      ),
    );
  }, [mounts, onUpdate]);

  const handleClear = useCallback(
    (index: number) => {
      setRowKeys((keys) => keys.filter((_, i) => i !== index));
      onUpdate(stringifyMounts(mounts.filter((_, i) => i !== index)));
    },
    [mounts, onUpdate],
  );

  return (
    <Stack spacing={3}>
      {mounts.map(({ dataId, sourcePath, mountPath, accessMode }, index) => (
        <DataMountsFieldRow
          key={rowKeys[index]}
          rowKey={rowKeys[index]}
          dataId={dataId}
          sourcePath={sourcePath}
          mountPath={mountPath}
          accessMode={accessMode}
          index={index}
          isMountPathDuplicated={
            mounts.filter((other) => other.mountPath === mountPath).length > 1
          }
          initialRecents={initialRecents}
          onInitialRecentsSeeded={clearLegacyRecents}
          onDataObjectSelected={handleDataObjectSelected}
          onDataResolved={handleDataResolved}
          onDataMountPathUpdated={(mountPath) =>
            updateMount(index, { mountPath })
          }
          onSourcePathUpdated={(sourcePath) =>
            updateMount(index, { sourcePath })
          }
          onAccessModeUpdated={(accessMode) =>
            updateMount(index, { accessMode })
          }
          onCleared={() => handleClear(index)}
        />
      ))}
      <AddRowButton color="primary" onClick={handleAdd}>
        <Add /> Add data mount
      </AddRowButton>
    </Stack>
  );
});

export default DataMountsField;
